import * as React from "react";
import { useIdleLogout } from "../../ui/hooks/useIdleLogout";
import { Suspense, lazy, useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { getValidAuthSession, type AuthFailureReason } from "../../core/auth/authGuard";
import { insyncTheme } from "../../ui/theme/insyncTheme";
import { logAction, setCurrentActivity, paneActivity, setAmbientContext } from "../../utils/actionLog";

const LoginPane = lazy(() => import("../../ui/loginPane/LoginPane"));
const InsertDataPane = lazy(() => import("../../ui/insertDataPane/InsertDataPane"));
const InsertFormulaPane = lazy(() => import("../../ui/insertFormulaPane/InsertFormulaPane"));
const StockPricesPane = lazy(() => import("../../ui/stockPricesPane/StockPricesPane"));
const ModelsPane = lazy(() => import("../../ui/modelsPane/ModelsPane"));
const CompsPane = lazy(() => import("../../ui/compsPane/CompsPane"));
const SourceDocumentsPane = lazy(() => import("../../ui/sourceDocumentsPane/SourceDocumentsPane"));
const BrokerRevisionsPane = lazy(() => import("../../ui/brokerRevisionsPane/BrokerRevisionsPane"));
const DynamicApiPane = lazy(() => import("../../ui/dynamicApiPane/DynamicApiPane"));
const AuditPane = lazy(() => import("../../ui/auditPane/AuditPane"));
const RefreshPane = lazy(() => import("../../ui/refreshPane/RefreshPane"));
const UnlinkPane = lazy(() => import("../../ui/unlinkPane/UnlinkPane"));
const RelinkPane = lazy(() => import("../../ui/relinkPane/RelinkPane"));
const ProfilePane = lazy(() => import("../../ui/profilePane/ProfilePane"));
const ContactPane = lazy(() => import("../../ui/contactPane/ContactPane"));
const LogoutPane = lazy(() => import("../../ui/logoutPane/LogoutPane"));
import { loginService } from '../../core/auth/loginService';
import { onSessionEnded } from '../../utils/sessionEnded';
// Pane types for routing
type PaneType = "login" | "insertData" | "insertFormula" | "stockPrices" | "models" | "comps" | "sourceDocuments" | "brokerRevisions" | "dynamicApi" | "audit" | "refresh" | "unlink" | "relink" | "profile" | "contact" | "logout";

const DEFAULT_AUTHENTICATED_PANE: PaneType = "profile";
const PENDING_ROUTE_STORAGE_KEY = "insync:pendingPaneRoute";
const LAST_ROUTE_STORAGE_KEY = "insync:lastPaneRoute";
const TASKPANE_HEARTBEAT_STORAGE_KEY = "insync:taskpaneHeartbeat";
const AUTH_REQUIRED_PANES: PaneType[] = [
  "insertData",
  "insertFormula",
  "stockPrices",
  "models",
  "comps",
  "sourceDocuments",
  "brokerRevisions",
  "dynamicApi",
  "audit",
  "refresh",
  "unlink",
  "relink",
  "profile",
  "contact",
];
const ALL_PANES: PaneType[] = ["login", ...AUTH_REQUIRED_PANES, "logout"];

function isPaneType(pane: string | null): pane is PaneType {
  return ALL_PANES.includes(pane as PaneType);
}

function isAuthRequiredPane(pane: string | null): pane is PaneType {
  return AUTH_REQUIRED_PANES.includes(pane as PaneType);
}

function buildRouteSearch(route: { pane?: string; mode?: string; action?: string }): string | null {
  if (!route.pane) {
    return null;
  }

  const params = new URLSearchParams();
  params.set("pane", route.pane);

  if (route.mode) {
    params.set("mode", route.mode);
  }

  if (route.action) {
    params.set("action", route.action);
  }

  return `?${params.toString()}`;
}

function readStoredRouteSearch(storageKey: string, removeAfterRead = false): string | null {
  try {
    const rawRoute = window.localStorage.getItem(storageKey);

    if (!rawRoute) {
      return null;
    }

    if (removeAfterRead) {
      window.localStorage.removeItem(storageKey);
    }

    return buildRouteSearch(JSON.parse(rawRoute));
  } catch (error) {
    console.warn("[Taskpane] Unable to read stored pane route:", error);
    return null;
  }
}

function consumePendingRouteSearch(): string | null {
  return readStoredRouteSearch(PENDING_ROUTE_STORAGE_KEY, true);
}

function readLastRouteSearch(): string | null {
  return readStoredRouteSearch(LAST_ROUTE_STORAGE_KEY);
}

function readPaneFromSearch(search: string | null): PaneType | null {
  if (!search) {
    return null;
  }

  const params = new URLSearchParams(search);
  const pane = params.get("pane");
  return isPaneType(pane) ? pane : null;
}

function saveLastRouteSearch(search: string | null) {
  if (!search) {
    return;
  }

  try {
    const params = new URLSearchParams(search);
    const pane = params.get("pane");

    if (!isPaneType(pane)) {
      return;
    }

    const route: { pane: PaneType; mode?: string; action?: string } = { pane };
    const mode = params.get("mode");
    const action = params.get("action");

    if (mode) {
      route.mode = mode;
    }

    if (action) {
      route.action = action;
    }

    window.localStorage.setItem(LAST_ROUTE_STORAGE_KEY, JSON.stringify(route));
  } catch (error) {
    console.warn("[Taskpane] Unable to save last pane route:", error);
  }
}

function replaceRouteSearch(search: string) {
  try {
    if (typeof window.history?.replaceState === "function") {
      window.history.replaceState({}, "", `${window.location.pathname}${search}`);
    }
  } catch (error) {
    console.warn("[Taskpane] Unable to update pane URL:", error);
  }
}

const App: React.FC = () => {
  const [activePane, setActivePane] = useState<PaneType>("login");
  const [requestedPane, setRequestedPane] = useState<PaneType>(DEFAULT_AUTHENTICATED_PANE);
  const [routeKey, setRouteKey] = useState(window.location.search || "?pane=login");
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isRouteReady, setIsRouteReady] = useState(false);
  // Set when an OTP session was logged out for inactivity, so the login screen
  // can say why. Cleared on the next successful login.
  const [loggedOutForInactivity, setLoggedOutForInactivity] = useState(false);
  // Set when the server ended the session (see utils/sessionEnded), for the same
  // reason. Cleared on the next successful login.
  const [sessionEnded, setSessionEnded] = useState(false);

  useEffect(() => {
    // Office.js initialization check
    const checkOfficeReady = () => {
      if (typeof Office !== "undefined" && Office.context) {
        setIsOfficeReady(true);
      } else {
        // Retry after a short delay if Office.js isn't loaded yet
        setTimeout(checkOfficeReady, 100);
      }
    };

    checkOfficeReady();
  }, []);

  useEffect(() => {
    const writeHeartbeat = () => {
      try {
        window.localStorage.setItem(TASKPANE_HEARTBEAT_STORAGE_KEY, String(Date.now()));
      } catch (error) {
        console.warn("[Taskpane] Unable to write taskpane heartbeat:", error);
      }
    };

    const clearHeartbeat = () => {
      try {
        window.localStorage.removeItem(TASKPANE_HEARTBEAT_STORAGE_KEY);
      } catch (error) {
        console.warn("[Taskpane] Unable to clear taskpane heartbeat:", error);
      }
    };

    writeHeartbeat();
    const heartbeatInterval = window.setInterval(writeHeartbeat, 2000);
    window.addEventListener("pagehide", clearHeartbeat);
    window.addEventListener("beforeunload", clearHeartbeat);

    return () => {
      window.clearInterval(heartbeatInterval);
      window.removeEventListener("pagehide", clearHeartbeat);
      window.removeEventListener("beforeunload", clearHeartbeat);
      clearHeartbeat();
    };
  }, []);

  // Why the router last sent the user to login, so Pane.Redirected can report
  // the actual cause instead of asserting a generic "session invalid".
  const redirectReasonRef = useRef<AuthFailureReason | null>(null);

  const resolvePaneFromLocation = useCallback(async () => {
    const pendingRouteSearch = consumePendingRouteSearch();
    const lastRouteSearch = readLastRouteSearch();
    const browserRouteSearch = window.location.search;
    const browserPane = readPaneFromSearch(browserRouteSearch);
    const lastPane = readPaneFromSearch(lastRouteSearch);
    const shouldRestoreLastRoute =
      !pendingRouteSearch &&
      browserPane === "login" &&
      lastRouteSearch &&
      isAuthRequiredPane(lastPane);
    const currentRouteSearch = pendingRouteSearch || (shouldRestoreLastRoute ? lastRouteSearch : browserRouteSearch) || lastRouteSearch || "?pane=login";

    if (pendingRouteSearch || shouldRestoreLastRoute || (!browserRouteSearch && lastRouteSearch)) {
      replaceRouteSearch(currentRouteSearch);
    }

    const params = new URLSearchParams(currentRouteSearch);
    const paneParam = params.get("pane");
    const pane = isPaneType(paneParam) ? paneParam : "login";
    setRouteKey(currentRouteSearch || "?pane=login");

    if (pane === "logout") {
      setActivePane("logout");
    } else if (isAuthRequiredPane(pane)) {
      setRequestedPane(pane);
      saveLastRouteSearch(currentRouteSearch);
      const session = await getValidAuthSession();
      if (session.loggedOutForInactivity) setLoggedOutForInactivity(true);
      redirectReasonRef.current = session.isAuthenticated ? null : session.reason ?? null;
      setActivePane(session.isAuthenticated ? pane : "login");
    } else {
      const session = await getValidAuthSession();
      if (session.loggedOutForInactivity) setLoggedOutForInactivity(true);

      if (session.isAuthenticated) {
        const fallbackRouteSearch = isAuthRequiredPane(lastPane)
          ? lastRouteSearch
          : buildRouteSearch({ pane: DEFAULT_AUTHENTICATED_PANE });
        const fallbackPane = readPaneFromSearch(fallbackRouteSearch) || DEFAULT_AUTHENTICATED_PANE;

        if (fallbackRouteSearch) {
          replaceRouteSearch(fallbackRouteSearch);
          saveLastRouteSearch(fallbackRouteSearch);
          setRouteKey(fallbackRouteSearch);
        }

        setRequestedPane(fallbackPane);
        setActivePane(fallbackPane);
      } else {
        setActivePane("login");
      }
    }

    setIsRouteReady(true);
  }, []);

  useEffect(() => {
    if (!isOfficeReady) {
      return;
    }

    void resolvePaneFromLocation();
  }, [isOfficeReady, resolvePaneFromLocation]);

  useEffect(() => {
    if (!isOfficeReady) {
      return;
    }

    const handlePaneNavigation = () => {
      void resolvePaneFromLocation();
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === PENDING_ROUTE_STORAGE_KEY) {
        void resolvePaneFromLocation();
      }
    };

    window.addEventListener("insync:navigate-pane", handlePaneNavigation);
    window.addEventListener("popstate", handlePaneNavigation);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("insync:navigate-pane", handlePaneNavigation);
      window.removeEventListener("popstate", handlePaneNavigation);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isOfficeReady, resolvePaneFromLocation]);

  useLayoutEffect(() => {
    if (!isRouteReady) {
      return;
    }

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activePane, routeKey, isRouteReady]);

  // Every ribbon button opens the SAME taskpane with a different ?pane= (see the
  // SourceLocation URLs in manifest.xml), so this ONE effect records all 17 of
  // them, plus in-app navigation. Instrumenting each button separately would be
  // 17 places to keep in sync and one of them would inevitably be missed.
  //
  // The ref guards against logging a re-render rather than a navigation: React
  // may re-run this effect with an unchanged routeKey, and a duplicated row per
  // render would make the counts meaningless.
  const lastLoggedRouteRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isRouteReady || lastLoggedRouteRef.current === routeKey) {
      return;
    }
    lastLoggedRouteRef.current = routeKey;

    const params = new URLSearchParams(routeKey);

    // What the user ASKED for, which is not always what they got: an expired or
    // not-yet-hydrated session makes resolvePaneFromLocation fall back to the
    // login pane while the route still says ?pane=models.
    //
    // Reporting activePane here would record that bounce as "Opened login" - a
    // ribbon click on a button the user never pressed. The requested pane is the
    // click; the divergence is a separate fact, logged separately below.
    const paneParam = params.get("pane");
    const requestedPane: PaneType = isPaneType(paneParam) ? paneParam : "login";
    // Only ONE divergence is worth a record: being bounced to the login pane,
    // which means the session was not valid. Every other mismatch is the router
    // normalising a route (opening the taskpane with no ?pane= and landing on
    // the default pane), which is not something that happened TO the user and
    // carries no information - it is logged as an ordinary open below.
    const bouncedToLogin = requestedPane !== activePane && activePane === "login";

    // Set the ambient flow BEFORE logging, so this event and every later one
    // from this pane - including API calls, whose logger context is just "API" -
    // carry the same activity. Filtering by activity='Comps' then returns the
    // whole Comps journey rather than only the rows that happen to name it.
    // Keyed on activePane, not requestedPane: ambient describes what is on
    // screen, and the events that follow come from the pane actually rendered.
    setCurrentActivity(paneActivity(activePane));
    // pane on every row from here on; ticker cleared so a selection made in one
    // pane is not still attached to events in the next.
    setAmbientContext({
      pane: activePane,
      mode: params.get("mode") ?? null,
      ticker: null,
    });

    if (bouncedToLogin) {
      // Deliberately NOT a Ribbon.Clicked. The user pressed a ribbon button and
      // was sent to login instead; recording it as a click on Login would both
      // invent a press that never happened and hide the session failure.
      logAction("Pane.Redirected", `Sent to login from ${requestedPane}`, {
        requested_pane: requestedPane,
        shown_pane: "login",
        // The real cause, from the auth guard. Note that none of these involve
        // the API rejecting a token: isSessionValid() is a purely LOCAL check of
        // the stored login timestamp against a 30-day window, so a server-side
        // rejection would surface as a 401 on the request itself, not here.
        reason: redirectReasonRef.current ?? "unknown",
        mode: params.get("mode") ?? undefined,
        action: params.get("action") ?? undefined,
      });
      return;
    }

    // activePane, not requestedPane: this records the pane the user is actually
    // looking at. They differ only when the router picked the default pane for a
    // route that named none, and the default is genuinely what was opened.
    logAction("Ribbon.Clicked", `Opened ${activePane}`, {
      pane: activePane,
      mode: params.get("mode") ?? undefined,
      action: params.get("action") ?? undefined,
    });
  }, [activePane, routeKey, isRouteReady]);

  const setCurrentRoute = useCallback((route: { pane: PaneType; mode?: string; action?: string }) => {
    const search = buildRouteSearch(route);

    if (!search) {
      return;
    }

    replaceRouteSearch(search);
    saveLastRouteSearch(search);
    setRouteKey(search);
  }, []);

  // OTP sessions: log out after inactivity and return to the login screen.
  useIdleLogout(() => {
    setLoggedOutForInactivity(true);
    setCurrentRoute({ pane: "login" });
    setActivePane("login");
  }, isOfficeReady);

  // Session ended server-side (logged out elsewhere, revoked, deactivated): a data
  // call got a session-ended 401 from the edge. Clear the local session and show
  // the login screen, instead of leaving a "logged in" pane whose calls all fail.
  useEffect(() => {
    return onSessionEnded(() => {
      void loginService.endRevokedSession().then(() => {
        setSessionEnded(true);
        setCurrentRoute({ pane: "login" });
        setActivePane("login");
      });
    });
  }, []);

  // Handle pane switching after login
  const handleLoginSuccess = async() => {
    setLoggedOutForInactivity(false);
    setSessionEnded(false);
    const nextPane = requestedPane || DEFAULT_AUTHENTICATED_PANE;
    setCurrentRoute({ pane: nextPane });
    setActivePane(nextPane);
  };

  const handleLoginRequired = () => {
    setActivePane("login");
  };

  const handleOpenLogin = () => {
    setCurrentRoute({ pane: "login" });
    setActivePane("login");
  };

  const handleOpenContactSupport = () => {
    setRequestedPane("contact");
    setCurrentRoute({ pane: "contact" });
    setActivePane("contact");
  };

  const loadingPane = (
    <div style={{ display: "grid", minHeight: "100vh", placeItems: "center", background: insyncTheme.color.bg, color: insyncTheme.color.text }}>
      <span>Loading</span>
    </div>
  );

  if (!isOfficeReady || !isRouteReady) {
    return (
      <div style={{ minHeight: "100vh", padding: "20px", textAlign: "center", backgroundColor: insyncTheme.color.bg, color: insyncTheme.color.text }}>
        <p>Loading IAD...</p>
        <p style={{ fontSize: "12px", color: insyncTheme.color.muted }}>
          Waiting for Office.js to initialize
        </p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: insyncTheme.color.bg, color: insyncTheme.color.text }}>
      <Suspense fallback={loadingPane}>
        {activePane === "login" && <LoginPane key={routeKey} loggedOutForInactivity={loggedOutForInactivity} sessionEnded={sessionEnded} onLoginSuccess={handleLoginSuccess} onOpenContactSupport={handleOpenContactSupport} />}
        {activePane === "insertData" && <InsertDataPane key={routeKey} onLogout={handleLoginRequired} />}
        {activePane === "insertFormula" && <InsertFormulaPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "stockPrices" && <StockPricesPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "models" && <ModelsPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "comps" && <CompsPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "sourceDocuments" && <SourceDocumentsPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "brokerRevisions" && <BrokerRevisionsPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "dynamicApi" && <DynamicApiPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "audit" && <AuditPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "refresh" && <RefreshPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "unlink" && <UnlinkPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "relink" && <RelinkPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "profile" && <ProfilePane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "contact" && <ContactPane key={routeKey} onLoginRequired={handleLoginRequired} />}
        {activePane === "logout" && <LogoutPane key={routeKey} onLoginRequested={handleOpenLogin} />}
      </Suspense>
    </div>
  );
};

export default App;
